import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, Product } from '../types';

interface CartState {
    items: CartItem[];

    // Actions
    addItem: (product: Product, quantity?: number) => void;
    addItems: (items: { product: Product; quantity: number }[]) => void;
    removeItem: (productId: string) => void;
    updateQuantity: (productId: string, quantity: number) => void;
    updatePiecesPerKoli: (productId: string, piecesPerKoli: number) => void;
    clearCart: () => void;

    // Computed
    getTotalItems: () => number;
    getTotalPrice: () => number;
    getSubtotal: () => number;
}

export const useCartStore = create<CartState>()(
    persist(
        (set, get) => ({
            items: [],

            addItem: (product: Product, quantity = 1) => {
                const items = get().items;
                const existingItem = items.find(item => item.productId === product.id);
                const defaultPieces = product.minPiecesPerBox || 12;

                if (existingItem) {
                    set({
                        items: items.map(item =>
                            item.productId === product.id
                                ? { ...item, quantity: item.quantity + quantity }
                                : item
                        ),
                    });
                } else {
                    set({
                        items: [...items, { productId: product.id, product, quantity, piecesPerKoli: defaultPieces }],
                    });
                }
            },

            addItems: (newItems) => {
                const currentItems = [...get().items];

                newItems.forEach(({ product, quantity }) => {
                    const defaultPieces = product.minPiecesPerBox || 12;
                    const existingIndex = currentItems.findIndex(i => i.productId === product.id);
                    if (existingIndex > -1) {
                        currentItems[existingIndex] = {
                            ...currentItems[existingIndex],
                            quantity: currentItems[existingIndex].quantity + quantity
                        };
                    } else {
                        currentItems.push({ productId: product.id, product, quantity, piecesPerKoli: defaultPieces });
                    }
                });

                set({ items: currentItems });
            },

            removeItem: (productId: string) => {
                set({
                    items: get().items.filter(item => item.productId !== productId),
                });
            },

            updateQuantity: (productId: string, quantity: number) => {
                if (quantity <= 0) {
                    get().removeItem(productId);
                    return;
                }

                set({
                    items: get().items.map(item =>
                        item.productId === productId
                            ? { ...item, quantity }
                            : item
                    ),
                });
            },

            updatePiecesPerKoli: (productId: string, piecesPerKoli: number) => {
                set({
                    items: get().items.map(item =>
                        item.productId === productId
                            ? { ...item, piecesPerKoli }
                            : item
                    ),
                });
            },

            clearCart: () => set({ items: [] }),

            getTotalItems: () => {
                return get().items.reduce((total, item) => total + item.quantity, 0);
            },

            getTotalPrice: () => {
                return get().items.reduce(
                    (total, item) => total + item.product.wholesalePrice * item.quantity,
                    0
                );
            },

            getSubtotal: () => {
                return get().items.reduce(
                    (total, item) => total + item.product.wholesalePrice * item.quantity,
                    0
                );
            },
        }),
        {
            name: 'cheesecake-cart',
        }
    )
);
